type WidgetSize = { width: number; height: number };

/**
 * A dashboard widget content area is "compact" when it is small in *either*
 * dimension. Below these thresholds the filter pills are floated out of flow so
 * the chart reclaims the space — which both keeps the pills usable on narrow
 * widgets (RSP-4068) and stops the donut chart's count label from being
 * squeezed into the chart on short widgets (RSP-4069). Tunable.
 */
export const COMPACT_WIDGET_WIDTH = 320;
export const COMPACT_WIDGET_HEIGHT = 260;

/**
 * Hysteresis band (px) applied when *leaving* the compact layout. Wider than any
 * scrollbar gutter so that floating the filter (which can remove a content
 * scrollbar and grow the measured width by ~15-17px) cannot push the widget back
 * over the threshold and flip it out of compact — which otherwise self-sustains
 * a flicker at the boundary on platforms with classic scrollbars (RSP-6086).
 */
export const COMPACT_WIDGET_HYSTERESIS = 40;

/**
 * Whether a widget should render in its compact (floated-filter) layout.
 * Returns `false` for an unmeasured size so widgets default to the inline
 * layout until their real size is known (no flash of the collapsed control).
 *
 * `wasCompact` applies hysteresis: a widget enters compact as soon as either
 * dimension is below its threshold, but only leaves once it clears *both*
 * thresholds by `COMPACT_WIDGET_HYSTERESIS`, so a sub-threshold size wobble
 * can't flip the layout back and forth.
 */
export const isWidgetCompact = (
  size: WidgetSize | undefined,
  wasCompact = false
): boolean => {
  if (size === undefined) {
    return false;
  }
  const margin = wasCompact ? COMPACT_WIDGET_HYSTERESIS : 0;

  return (
    size.width < COMPACT_WIDGET_WIDTH + margin ||
    size.height < COMPACT_WIDGET_HEIGHT + margin
  );
};
